/**
 * Teacher letters ("Send to Teacher Juno").
 *
 * State machine: charging (envelope energy < size) → ready (full: pick 5 drawings) → sent (pending
 * until deliverAt) → delivered (letter in the journal, energy resets) → charging ...
 * Letters are composed locally from self-written templates: milestone (1..4 by letters received) ×
 * variant (1..3), personalised with the name, age and the subjects of the sent drawings.
 */
import { ENVELOPE_DRAWINGS_TO_FILL, LETTER_DELIVERY_HOURS, LETTER_DRAWINGS, LETTER_MILESTONES, LETTER_VARIANTS } from './constants';
import type { LetterTemplates, SkillId } from './content/model';
import { fill, localize, type Lang } from './text';
import { joinList } from './i18n/locales';

export interface SentDrawing {
  journalId: string;
  subject: string;
  skill: SkillId;
}

export interface PendingLetter {
  id: string;
  sentAt: string;
  deliverAt: string;
  drawings: SentDrawing[];
}

export interface Letter {
  id: string;
  milestone: number; // 1..4
  variant: number; // 1..3
  sentAt: string;
  deliveredAt: string;
  drawings: SentDrawing[];
  /** Note template index per drawing (stable so the letter reads the same every time). */
  noteIdx: number[];
  opened: boolean;
}

export interface MailingState {
  energy: number;
  pending: PendingLetter | null;
  letters: Letter[];
}

export type MailingPhase = 'charging' | 'ready' | 'sent';

export const emptyMailing = (): MailingState => ({ energy: 0, pending: null, letters: [] });

export function mailingPhase(m: MailingState, size = ENVELOPE_DRAWINGS_TO_FILL): MailingPhase {
  if (m.pending) return 'sent';
  return m.energy >= size ? 'ready' : 'charging';
}

export const drawingsUntilEnvelopeFull = (m: MailingState, size = ENVELOPE_DRAWINGS_TO_FILL) => Math.max(0, size - m.energy);

/** Send the selected drawings (must be exactly LETTER_DRAWINGS and the envelope must be full). */
export function sendLetter(m: MailingState, drawings: SentDrawing[], now: Date, id: string, opts: { delayHours?: number; size?: number } = {}): MailingState {
  if (mailingPhase(m, opts.size) !== 'ready') throw new Error('envelope_not_ready');
  if (drawings.length !== LETTER_DRAWINGS) throw new Error('select_exactly_5');
  if (new Set(drawings.map((d) => d.journalId)).size !== drawings.length) throw new Error('duplicate_drawing');
  const deliverAt = new Date(now.getTime() + (opts.delayHours ?? LETTER_DELIVERY_HOURS) * 3600_000);
  return { ...m, pending: { id, sentAt: now.toISOString(), deliverAt: deliverAt.toISOString(), drawings } };
}

/** Deliver the pending letter if its time has come (or `force`, the debug "deliver now" switch). */
export function deliverDue(m: MailingState, now: Date, force = false): { state: MailingState; delivered: Letter | null } {
  const p = m.pending;
  if (!p || (!force && new Date(p.deliverAt).getTime() > now.getTime())) return { state: m, delivered: null };
  const n = m.letters.length;
  const milestone = Math.min(LETTER_MILESTONES, n + 1);
  const variant = (hash(p.id) % LETTER_VARIANTS) + 1;
  const letter: Letter = {
    id: p.id,
    milestone,
    variant,
    sentAt: p.sentAt,
    deliveredAt: now.toISOString(),
    drawings: p.drawings,
    noteIdx: p.drawings.map((d, i) => hash(d.journalId + i)),
    opened: false,
  };
  return { state: { energy: 0, pending: null, letters: [...m.letters, letter] }, delivered: letter };
}

export function openLetter(m: MailingState, id: string): MailingState {
  return { ...m, letters: m.letters.map((l) => (l.id === id ? { ...l, opened: true } : l)) };
}

export const unopenedLetters = (m: MailingState) => m.letters.filter((l) => !l.opened).length;

export interface ComposedLetter {
  body: string;
  notes: { journalId: string; text: string }[];
  signoff: string;
}

export function composeLetter(letter: Letter, tpl: LetterTemplates, ctx: { name: string; age: number | null; lang: Lang }): ComposedLetter {
  const subjects = [...new Set(letter.drawings.map((d) => d.subject))];
  const list = joinList(subjects, ctx.lang);
  const variants = tpl.milestones[Math.min(3, Math.max(0, letter.milestone - 1)) as 0 | 1 | 2 | 3];
  const body = variants[(letter.variant - 1) % variants.length]!;
  const vars = { name: ctx.name, age: ctx.age ?? '', subjects: list, count: letter.drawings.length, teacher: 'Juno' };
  return {
    body: fill(localize(body, ctx.lang), vars),
    notes: letter.drawings.map((d, i) => {
      const pool = tpl.notes[d.skill];
      const note = pool[(letter.noteIdx[i] ?? 0) % pool.length]!;
      return { journalId: d.journalId, text: fill(localize(note, ctx.lang), { name: ctx.name, subject: d.subject }) };
    }),
    signoff: localize(tpl.signoffs[(letter.variant - 1) % tpl.signoffs.length]!, ctx.lang),
  };
}



/** Small deterministic string hash (FNV-1a). */
export function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function mergeMailing(a: MailingState, b: MailingState): MailingState {
  const letters = [...a.letters];
  for (const l of b.letters) {
    const i = letters.findIndex((x) => x.id === l.id);
    if (i < 0) letters.push(l);
    else letters[i] = { ...letters[i]!, opened: letters[i]!.opened || l.opened };
  }
  letters.sort((x, y) => x.deliveredAt.localeCompare(y.deliveredAt));
  const delivered = new Set(letters.map((l) => l.id));
  const pending = [a.pending, b.pending].find((p) => p && !delivered.has(p.id)) ?? null;
  // Energy resets on delivery, so the snapshot that has seen more letters knows the current energy.
  const energy = a.letters.length === b.letters.length ? Math.max(a.energy, b.energy) : a.letters.length > b.letters.length ? a.energy : b.energy;
  return { energy: pending ? 0 : energy, pending, letters };
}
